import EditIcon from '@mui/icons-material/Edit';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Fade, IconButton, Input, LinearProgress, List, ListItem, ListItemText, Switch, TableCell, TableRow, Typography } from "@mui/material";
import { inject, observer } from "mobx-react";
import React, { useState, useContext } from "react";
import { FormattedNumber } from "react-intl";
import { Link } from "react-router-dom";
import { usePostV4ManufacturerByRouteManufacturerIdPurchaseOrderAndPurchaseOrderNumberEditItemRandmarSkuMutation } from "randmar-api-client";
import { useToast } from "randmarcomps";
import ManufacturerContext from "../../contexts/ManufacturerContext";
import Path from "../../routes/Path";
import { PurchaseOrderPartNumber } from "../../stores/document/Document";
import { RootStore } from "../../stores/rootStore";
import ProductDetailsModal from '../products/ProductDetailsModal';
import ButtonWithDialogConfirm from "../util/ButtonWithDialogConfirm";
import CurrencyUtils from "../util/CurrencyUtils";

interface IProps {
  store?: RootStore;
  partNumber: PurchaseOrderPartNumber;
  purchaseOrderNumber: string;
}

const PurchaseOrderPartNumberRow: React.FC<IProps> = ({ store, partNumber, purchaseOrderNumber }) => {
  const { applicationId } = useContext(ManufacturerContext);
  const [updateStandardCost, setUpdateStandardCost] = useState(false);
  const [openUpdate, setOpenUpdate] = useState(false);
  const [count, setCount] = useState(partNumber.quantity || 0);
  const [openEditUnitPrice, setOpenEditUnitPrice] = useState(false);
  const [unitPrice, setUnitPrice] = useState(partNumber.unitPrice || -1);
  const { toast } = useToast();

  const [editItem, { isLoading: isEditing }] = usePostV4ManufacturerByRouteManufacturerIdPurchaseOrderAndPurchaseOrderNumberEditItemRandmarSkuMutation();

  const handleEditUnitPriceIcon = () => {
    setOpenEditUnitPrice(true);
  };

  const handleUnitPriceChange = (newValue: number) => {
    if (isNaN(newValue) || newValue < 0) newValue = -1;
    setUnitPrice(parseFloat(newValue.toFixed(2)));
  };

  const handleUpdateButton = () => {
    setOpenUpdate(true);
  };

  const handleCancel = () => {
    setOpenEditUnitPrice(false);
    setOpenUpdate(false);
  };

  const handleOnCountChanged = (newValue: number) => {
    if (isNaN(newValue) || newValue < 0) newValue = 0;
    setCount(newValue);
  };

  const handleUnitPriceConfirm = async () => {
    try {
      await editItem({
        routeManufacturerId: applicationId,
        purchaseOrderNumber,
        randmarSku: partNumber.randmarSKU || "",
        unitPrice,
        editStandardCost: updateStandardCost
      }).unwrap();
      toast({ variant: "success", title: "Success", description: "Unit price updated successfully." });
    } catch (err) {
      toast({ variant: "destructive", title: "Error", description: "Could not update unit price." });
    }

    setOpenEditUnitPrice(false);
  };

  const handleConfirm = async (operation: string): Promise<void> => {
    let newValue = 0;
    let messageAdded = `${partNumber.mpn} removed from purchase order ${purchaseOrderNumber}`;
    let messageNotAdded = `${partNumber.mpn} could not be removed from purchase order ${purchaseOrderNumber}`;

    if (operation === "update") {
      const quantityDifference = count - (partNumber.quantity || 0);
      newValue = count;

      if (quantityDifference > 0) {
        messageAdded = `${quantityDifference} x ${partNumber.mpn} added to purchase order ${purchaseOrderNumber}`;
        messageNotAdded = `${quantityDifference} x ${partNumber.mpn} could not be added to purchase order ${purchaseOrderNumber}`;
      } else {
        messageAdded = `${quantityDifference * -1} x ${partNumber.mpn} removed from purchase order ${purchaseOrderNumber}`;
        messageNotAdded = `${quantityDifference * -1} x ${partNumber.mpn} could not be removed from purchase order ${purchaseOrderNumber}`;
      }
    }

    if (newValue !== partNumber.quantity || operation === "delete") {
      try {
        await editItem({
          routeManufacturerId: applicationId,
          purchaseOrderNumber,
          randmarSku: partNumber.randmarSKU || "",
          quantity: newValue
        }).unwrap();
        toast({ variant: "success", title: "Success", description: messageAdded });
      } catch (err) {
        toast({ variant: "destructive", title: "Error", description: messageNotAdded });
      }
    }
    setOpenUpdate(false);
  };

  const { userStore } = store!;
  const isAdmin = userStore.isAdmin() || false;

  return (
    <TableRow sx={{ "&:hover": { backgroundColor: "#f9f5ff" } }} style={partNumber.category === "GRY" ? { backgroundColor: "yellow" } : undefined}>
      <TableCell>
        <List dense disablePadding sx={{ width: '100%' }}>
          <ListItem>
            <ListItemText
              primary={
                <Link to={Path.product(applicationId, partNumber.randmarSKU)}>
                  {partNumber.mpn}
                </Link>
              }
              secondary={partNumber.description} />
          </ListItem>
        </List>
      </TableCell>
      <TableCell align="center">
        <Input
          style={{ width: "4rem" }}
          inputProps={{ min: 0, style: { textAlign: 'center' } }}
          type="number"
          value={count}
          onChange={e => handleOnCountChanged(parseInt(e.target.value))}
        />
      </TableCell>
      <TableCell align="center" style={{ width: "150px", padding: 0 }}>
        <Fade in={count !== partNumber.quantity}>
          <Button
            id="ButtonConfirmItemChange"
            color="primary"
            variant="contained"
            onClick={handleUpdateButton}
            disabled={openUpdate}>
            Update
          </Button>
        </Fade>
        <Dialog
          open={openUpdate}
          onClose={isEditing ? undefined : handleCancel}
          aria-labelledby="form-dialog-title"
        >
          <DialogTitle id="form-dialog-title" style={{ textAlign: "center" }}>
            Update {partNumber.mpn} quantity?
          </DialogTitle>
          <div hidden={isEditing}>
            <DialogActions style={{ display: "flex", justifyContent: "space-between" }}>
              <Button onClick={handleCancel} color="primary">
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={() => handleConfirm("update")}
                color="primary">
                Confirm
              </Button>
            </DialogActions>
          </div>
          <div hidden={!isEditing} style={{ textAlign: "center" }}>
            <Typography>Please wait...</Typography>
            <LinearProgress />
          </div>
        </Dialog>
      </TableCell>
      <TableCell align="center">
        <FormattedNumber {...CurrencyUtils.format} value={partNumber.unitPrice || 0} />

        {isAdmin &&
          <>

            <IconButton color="primary" onClick={handleEditUnitPriceIcon} sx={{ ml: 1 }}>
              <EditIcon />
            </IconButton>

            <Dialog
              open={openEditUnitPrice}
              onClose={isEditing ? undefined : handleCancel}
              aria-labelledby="form-dialog-title"
            >
              <DialogTitle id="form-dialog-title" style={{ textAlign: "center" }}>
                {`Edit unit price of ${partNumber.mpn} for purchase order ${purchaseOrderNumber}`}
              </DialogTitle>
              <div hidden={isEditing}>
                <DialogContent>
                  <div>
                    Change unit price to : &nbsp;
                    <Input
                      style={{ width: "6rem" }}
                      value={unitPrice !== -1 ? unitPrice : ""}
                      inputProps={{ min: 0, style: { textAlign: 'center' } }}
                      type="number"
                      onChange={e => handleUnitPriceChange(parseFloat(e.target.value))}
                    />
                  </div>
                  <div>
                    Adjust Standard Cost to
                    <Switch disabled={unitPrice === partNumber.unitPrice} checked={updateStandardCost} onChange={() => setUpdateStandardCost(!updateStandardCost)} />
                  </div>
                </DialogContent>
                <DialogActions style={{ display: "flex", justifyContent: "space-between" }}>
                  <Button onClick={handleCancel} color="primary">
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleUnitPriceConfirm}
                    color="primary"
                    disabled={unitPrice <= 0}>
                    Confirm
                  </Button>
                </DialogActions>
              </div>
              <div hidden={!isEditing} style={{ textAlign: "center" }}>
                <Typography>Please wait...</Typography>
                <LinearProgress />
              </div>
            </Dialog>
          </>
        }

      </TableCell>

      <TableCell align="center">
        <FormattedNumber {...CurrencyUtils.format} value={(partNumber.unitPrice || 0) * (partNumber.quantity || 0)} />
      </TableCell>
      <TableCell align="center"><ProductDetailsModal randmarSKU={partNumber.randmarSKU || ""} /></TableCell>

      <TableCell align="center" style={{ padding: "0 20px 0 0" }}>
        <ButtonWithDialogConfirm
          fab={true}
          onConfirmation={() => handleConfirm("delete")}
          buttonTitle={`Remove ${partNumber.mpn} from ${purchaseOrderNumber}`}
        />
      </TableCell>
    </TableRow >
  );
};

export default inject("store")(observer(PurchaseOrderPartNumberRow));
